/**
 * The live record (`D243`, which widens `D209`'s B10): which of the
 * person's records a set about to be logged beats — PURE.
 *
 * ── THE MODULE'S RECORDS, RESTATED ─────────────────────────────────────────
 * The kinds are exactly the per-set marks `@repo/training`'s
 * `exercise-records.ts` keeps for the exercise sheet's "Recordes" tab, read
 * the same way, so the workout can never celebrate a record the sheet does
 * not then show:
 *
 * | kind       | the number                     | the module's mark       |
 * |------------|--------------------------------|-------------------------|
 * | `load`     | the load as LOGGED             | `heaviestLoad`          |
 * | `e1rm`     | Epley over the e1RM load       | `bestE1rm`              |
 * | `repMax`   | the e1RM load for ≥ N reps     | `repMaxes[N]`, N ≤ 12   |
 * | `reps`     | the reps of one set            | `mostReps`              |
 * | `volume`   | load × reps, both sides        | `bestSetVolume`         |
 * | `duration` | one hold, seconds              | `longestDuration`       |
 * | `distance` | one set, metres                | `longestDistance`       |
 *
 * `repMax` is "more reps at this weight": a set of N reps at L beats it when
 * no earlier set at L or heavier reached N reps — which is the heaviest load
 * for N reps going up, the module's own table. It is only said where an
 * earlier set at L or heavier EXISTS; otherwise the set is the heaviest ever
 * and `load` and `e1rm` already say so.
 *
 * The app cannot import a module (`D118`), so this is the third restatement
 * after `load-semantics.ts` and `rpe-anomaly.ts`, and its test restates the
 * module's rows.
 *
 * ── WHAT IS NEVER CELEBRATED ───────────────────────────────────────────────
 * - A warm-up (`D209`): it is not the lift.
 * - A first time: an exercise with no history, or a kind the history never
 *   measured (the first weighted set after bodyweight ones). The summary
 *   celebrates a first ever; a toast on every first set of a new account
 *   would be noise (`D209`, B10).
 * - A heavier `assisted` load: more assistance is LESS weight lifted, so
 *   only `e1rm` and `repMax`, which read body − help, judge that lift.
 * - A tie. Strictly better, as the module's marks keep the earlier set.
 *
 * ── OFFLINE, FROM WHAT THE DEVICE HOLDS (`D194`) ───────────────────────────
 * The floor is folded from the cached exercise history (180 days, today's
 * session left out) and raised by the all-time records the exercise sheet
 * left in the Query cache, when there are any. Today's own working sets
 * raise it again at the moment of the set, so a record is announced once.
 */
import {
  countsTowardTotals,
  type DeviceLoadSemantics,
  effectiveLoad,
  setE1rm,
  setVolume,
} from "@repo/client/session";
import type { ExerciseRecordsOutput } from "@repo/contract/training";

/** The rep counts `repMax` covers — the module's `REP_MAX_REPS`. */
export const LIVE_REP_MAX_REPS = 12;

export type RecordKind =
  | "distance"
  | "duration"
  | "e1rm"
  | "load"
  | "repMax"
  | "reps"
  | "volume";

/** The order a set's records are told in: the one a lifter brags about first. */
const RECORD_KIND_ORDER: readonly RecordKind[] = [
  "load",
  "e1rm",
  "repMax",
  "reps",
  "volume",
  "duration",
  "distance",
];

/** The best the person has done on one exercise, per kind; `null` = never. */
export interface RecordFloor {
  readonly distance: number | null;
  readonly duration: number | null;
  readonly e1rm: number | null;
  readonly load: number | null;
  /** Index `N − 1`: the heaviest e1RM-purpose load lifted for at least N
   *  reps, N = 1..{@link LIVE_REP_MAX_REPS}. Never rises as N does. */
  readonly repMaxes: readonly (number | null)[];
  readonly reps: number | null;
  readonly volume: number | null;
}

/** A set as the floor reads it — a logged set and a history row both fit. */
export interface RecordSet {
  readonly distanceMetres?: number | null | undefined;
  readonly durationSeconds?: number | null | undefined;
  readonly exerciseId: string;
  readonly kind?: string | null | undefined;
  readonly load: number | null;
  readonly reps: number | null;
  readonly side?: string | null | undefined;
}

/** One record a set beats. */
export interface LiveRecord {
  readonly kind: RecordKind;
  /** What it was; the set itself is the new value. */
  readonly previous: number;
  /** For `repMax`, the rep count it is about. */
  readonly reps?: number | undefined;
  readonly value: number;
}

export const EMPTY_FLOOR: RecordFloor = {
  distance: null,
  duration: null,
  e1rm: null,
  load: null,
  repMaxes: Array.from({ length: LIVE_REP_MAX_REPS }, () => null),
  reps: null,
  volume: null,
};

function max(held: number | null, next: number | null): number | null {
  if (next === null) {
    return held;
  }
  return held === null || next > held ? next : held;
}

function positive(value: number | null | undefined): number | null {
  return value !== null && value !== undefined && value > 0 ? value : null;
}

/** The narrowings `@repo/client/session` asks for, from a looser row. */
function asSet(set: RecordSet) {
  return {
    exerciseId: set.exerciseId,
    kind:
      set.kind === "warmup" || set.kind === "drop" || set.kind === "failure"
        ? set.kind
        : null,
    load: set.load,
    reps: set.reps,
    side: set.side === "left" || set.side === "right" ? set.side : null,
  } as const;
}

/** The e1RM-purpose load of a set — what the rep-max table is in. */
function e1rmLoad(set: RecordSet, semantics: DeviceLoadSemantics) {
  return effectiveLoad({
    bodyWeightKg: semantics.bodyWeightKg,
    load: set.load,
    loadMode: semantics.loadModeOf(set.exerciseId),
    purpose: "e1rm",
  });
}

/** What one set measures, per kind (`null` where it measures nothing). */
function measuresOf(
  set: RecordSet,
  semantics: DeviceLoadSemantics
): Omit<RecordFloor, "repMaxes"> & { readonly repMaxLoad: number | null } {
  const narrowed = asSet(set);
  const assisted = semantics.loadModeOf(set.exerciseId) === "assisted";
  return {
    distance: positive(set.distanceMetres),
    duration: positive(set.durationSeconds),
    e1rm: setE1rm(narrowed, semantics),
    load: assisted ? null : set.load,
    repMaxLoad:
      set.reps !== null && set.reps >= 1 ? e1rmLoad(set, semantics) : null,
    reps: positive(set.reps),
    volume: positive(setVolume(narrowed, semantics)),
  };
}

/** The floor with one more set in it. A warm-up leaves it as it was. */
export function foldSet(
  floor: RecordFloor,
  set: RecordSet,
  semantics: DeviceLoadSemantics
): RecordFloor {
  if (!countsTowardTotals(asSet(set))) {
    return floor;
  }
  const m = measuresOf(set, semantics);
  const reach = Math.min(set.reps ?? 0, LIVE_REP_MAX_REPS);
  return {
    distance: max(floor.distance, m.distance),
    duration: max(floor.duration, m.duration),
    e1rm: max(floor.e1rm, m.e1rm),
    load: max(floor.load, m.load),
    repMaxes: floor.repMaxes.map((held, index) =>
      index < reach ? max(held, m.repMaxLoad) : held
    ),
    reps: max(floor.reps, m.reps),
    volume: max(floor.volume, m.volume),
  };
}

/** Each exercise's floor from the history, `currentSessionId`'s sets left out. */
export function recordFloorsOf(
  sets: readonly (RecordSet & { readonly sessionId: string })[],
  currentSessionId: string,
  semantics: DeviceLoadSemantics
): Record<string, RecordFloor> {
  const floors: Record<string, RecordFloor> = {};
  for (const set of sets) {
    if (set.sessionId === currentSessionId) {
      continue;
    }
    floors[set.exerciseId] = foldSet(
      floors[set.exerciseId] ?? EMPTY_FLOOR,
      set,
      semantics
    );
  }
  return floors;
}

/**
 * The floor raised by the server's all-time marks for the exercise (the
 * sheet's cached `GET /me/exercise-records`). A mark made by the session
 * still running is skipped: it is today's, and today is judged set by set.
 */
export function withAllTimeRecords(
  floor: RecordFloor | undefined,
  output: ExerciseRecordsOutput,
  currentSessionId: string
): RecordFloor | undefined {
  const { records, repMaxes, sessions } = output;
  if (sessions.length === 0) {
    return floor;
  }
  const markValue = (mark: { sessionId: string; value: number } | null) =>
    mark === null || mark.sessionId === currentSessionId ? null : mark.value;
  const base = floor ?? EMPTY_FLOOR;
  return {
    distance: max(base.distance, markValue(records.longestDistance)),
    duration: max(base.duration, markValue(records.longestDuration)),
    e1rm: max(base.e1rm, markValue(records.bestE1rm)),
    load: max(base.load, markValue(records.heaviestLoad)),
    repMaxes: base.repMaxes.map((held, index) =>
      max(
        held,
        markValue(repMaxes.find((row) => row.reps === index + 1)?.best ?? null)
      )
    ),
    reps: max(base.reps, markValue(records.mostReps)),
    volume: max(base.volume, markValue(records.bestSetVolume)),
  };
}

/**
 * The records `candidate` beats, in {@link RECORD_KIND_ORDER}. `floor` is
 * the exercise's best before today, `undefined` when it was never logged;
 * `today` is every set logged today (any exercise; only the candidate's
 * count), which raises the bar so a record is said once.
 */
export function newRecords(
  floor: RecordFloor | undefined,
  today: readonly RecordSet[],
  candidate: RecordSet,
  semantics: DeviceLoadSemantics
): readonly LiveRecord[] {
  if (floor === undefined || !countsTowardTotals(asSet(candidate))) {
    return [];
  }
  let bar = floor;
  for (const set of today) {
    if (set.exerciseId === candidate.exerciseId) {
      bar = foldSet(bar, set, semantics);
    }
  }
  const m = measuresOf(candidate, semantics);
  const found: LiveRecord[] = [];
  const beat = (
    kind: RecordKind,
    value: number | null,
    held: number | null
  ) => {
    if (value !== null && held !== null && value > held) {
      found.push({ kind, previous: held, value });
    }
  };
  beat("load", m.load, bar.load);
  beat("e1rm", m.e1rm, bar.e1rm);
  const reps = candidate.reps ?? 0;
  const heaviest = bar.repMaxes[0] ?? null;
  if (
    m.repMaxLoad !== null &&
    heaviest !== null &&
    heaviest >= m.repMaxLoad &&
    reps >= 1
  ) {
    // Only where an earlier set was as heavy or heavier: see the header.
    const n = Math.min(reps, LIVE_REP_MAX_REPS);
    const held = bar.repMaxes[n - 1] ?? null;
    if (held === null || m.repMaxLoad > held) {
      const before = previousRepsAt(bar, m.repMaxLoad);
      if (before !== null && reps > before) {
        found.push({
          kind: "repMax",
          previous: before,
          reps,
          value: reps,
        });
      }
    }
  }
  beat("reps", m.reps, bar.reps);
  beat("volume", m.volume, bar.volume);
  beat("duration", m.duration, bar.duration);
  beat("distance", m.distance, bar.distance);
  return found.sort(
    (a, b) =>
      RECORD_KIND_ORDER.indexOf(a.kind) - RECORD_KIND_ORDER.indexOf(b.kind)
  );
}

/**
 * The most reps an earlier set reached at `load` or heavier, read off the
 * rep-max table: the largest N whose heaviest load is at least `load`.
 * `null` when no earlier set was that heavy.
 */
function previousRepsAt(floor: RecordFloor, load: number): number | null {
  let best: number | null = null;
  for (const [index, held] of floor.repMaxes.entries()) {
    if (held !== null && held >= load) {
      best = index + 1;
    }
  }
  return best;
}
